import {
    Sparkles,
    Sword,
    Crown,
    Coins,
    Gem,
    BookOpen,
    Trophy,
    MessageSquare,
    Gift,
    ShoppingBag,
    CreditCard,
    ShieldCheck,
    HelpCircle,
    Layers,
    Shirt,
    Activity,
    Flag,
    Vote,
    Newspaper,
    Compass,
    LucideIcon
} from "lucide-react"

export interface NavItem {
    label: string
    href: string
    description: string
    icon: LucideIcon
    badge?: {
        text: string
        variant: "amber" | "emerald" | "purple" | "blue" | "rose"
    }
    isExternal?: boolean
}

export interface NavPillar {
    id: string
    label: string
    icon: LucideIcon
    href?: string
    description: string
    featured?: NavItem
    items: NavItem[]
}

export const NAVIGATION_PILLARS: NavPillar[] = [
    {
        id: "game-info",
        label: "Game Info",
        icon: Sword,
        description: "Compendium, visual showcases, crafting, and server mechanics",
        featured: {
            label: "Character Auras",
            href: "/guides/auras",
            description: "Browse custom glowing aura VFX, stat amplifiers, and tier rankings.",
            icon: Sparkles,
            badge: { text: "POPULAR", variant: "amber" }
        },
        items: [
            {
                label: "Character Auras",
                href: "/guides/auras",
                description: "Interactive showcase of all in-game aura VFX & stat boosts",
                icon: Sparkles,
                badge: { text: "SHOWCASE", variant: "amber" }
            },
            {
                label: "Appearances & Skins",
                href: "/guides/appearances",
                description: "Custom weapon skins, costumes, cloaks & direct web purchase",
                icon: Shirt,
                badge: { text: "BUY ON SITE", variant: "purple" }
            },
            {
                label: "Guides & Tutorials Hub",
                href: "/guides",
                description: "Comprehensive class progression, session zones, and leveling tips",
                icon: BookOpen
            },
            {
                label: "God Jewels & Crafting",
                href: "/guides/crafting",
                description: "Endgame jewelry enhancement, recipes, and material directories",
                icon: Gem
            },
            {
                label: "Cave of Trials",
                href: "/guides/cave-of-trials",
                description: "Exclusive instanced dungeon rules, key farming & boss mechanics",
                icon: Compass
            },
            {
                label: "Server Rules & Fair Play",
                href: "/rules",
                description: "Dual-box limits, PvP regulations, and server security standards",
                icon: ShieldCheck
            }
        ]
    },
    {
        id: "world-warfare",
        label: "World & Warfare",
        icon: Crown,
        description: "Competitive PvP, castle sieges, raid schedules, and rankings",
        featured: {
            label: "Castle Sieges",
            href: "/siege",
            description: "Live fortress ownership, castle tax rates, and upcoming battle countdowns.",
            icon: Crown,
            badge: { text: "LIVE TIMERS", variant: "rose" }
        },
        items: [
            {
                label: "Castle Sieges",
                href: "/siege",
                description: "Real-time castle owners, tax treasury, and battle countdowns",
                icon: Crown,
                badge: { text: "LIVE SIEGE", variant: "rose" }
            },
            {
                label: "Raid Boss Spawns",
                href: "/bosses",
                description: "Epic world boss respawn schedules, locations & legendary drops",
                icon: Sword,
                badge: { text: "DROP TABLES", variant: "blue" }
            },
            {
                label: "Hero Rankings",
                href: "/ranking",
                description: "Top PvP killers, Olympiad nobles, and dominant server clans",
                icon: Trophy
            },
            {
                label: "Clan Migration Transfers",
                href: "/dashboard/clan-migrate",
                description: "Exclusive transition packages & voice channels for incoming clans",
                icon: Flag,
                badge: { text: "CLAN BONUS", variant: "emerald" }
            },
            {
                label: "Live Server Status",
                href: "/#status",
                description: "Current online heroes, low-latency node health & 99.9% uptime",
                icon: Activity
            }
        ]
    },
    {
        id: "rewards-shop",
        label: "Rewards & Shop",
        icon: Gift,
        description: "Donation store, daily voting bonuses, and starter pack rewards",
        featured: {
            label: "Free Starter Pack",
            href: "/#starter-pack",
            description: "Instantly claim +15 Trinity Weapon, 10B Adena, and 100k Einhasad Coins.",
            icon: Gift,
            badge: { text: "FREE BOOST", variant: "emerald" }
        },
        items: [
            {
                label: "Premium Store",
                href: "/shop",
                description: "Character services, cosmetics, and instant balance delivery",
                icon: ShoppingBag,
                badge: { text: "STORE", variant: "amber" }
            },
            {
                label: "Add Balance / Donate",
                href: "/dashboard/balance",
                description: "Credit top-up with Stripe, PayPal (+5% bonus), or Binance Pay",
                icon: CreditCard,
                badge: { text: "40% SALE", variant: "rose" }
            },
            {
                label: "Vote for Rewards",
                href: "/vote",
                description: "Vote on 5 top ranking sites every 12h for Blue Eva & power runes",
                icon: Vote,
                badge: { text: "DAILY FREE", variant: "emerald" }
            },
            {
                label: "Free Starter Pack",
                href: "/#starter-pack",
                description: "Claim +15 Trinity Weapon, 10 Billion Adena & 30-day PvE Rune",
                icon: Gift,
                badge: { text: "NEW HEROES", variant: "amber" }
            },
            {
                label: "Social Milestone Rewards",
                href: "/rewards",
                description: "Follow Facebook & Discord to unlock shared community gift packs",
                icon: Coins
            }
        ]
    },
    {
        id: "community",
        label: "Community",
        icon: MessageSquare,
        description: "Official announcements, Discord community, and support services",
        featured: {
            label: "Official Discord",
            href: "https://discord.gg/QbwJJHY52A",
            description: "Join 1,200+ players, automated giveaways, voice rooms, and 24/7 AI help.",
            icon: MessageSquare,
            badge: { text: "1.2K ONLINE", variant: "blue" },
            isExternal: true
        },
        items: [
            {
                label: "News & Patch Notes",
                href: "/news",
                description: "Server balance updates, maintenance reports, and expansion notices",
                icon: Newspaper,
                badge: { text: "UPDATES", variant: "blue" }
            },
            {
                label: "Discord Community",
                href: "https://discord.gg/QbwJJHY52A",
                description: "Join 1,200+ members, live voice channels, and 24/7 Dragon AI",
                icon: MessageSquare,
                badge: { text: "1.2K ONLINE", variant: "blue" },
                isExternal: true
            },
            {
                label: "FAQ & Knowledge Base",
                href: "/faq",
                description: "Troubleshooting guide, connection issues, and setup walkthroughs",
                icon: HelpCircle
            },
            {
                label: "Account Protection & Security",
                href: "/security",
                description: "PIN codes, HWID device locking, and secondary authentication",
                icon: ShieldCheck
            },
            {
                label: "Contact Staff Support",
                href: "/contact",
                description: "Direct assistance, ban reviews, and bug reporting desk",
                icon: HelpCircle
            }
        ]
    }
]

export const QUICK_ACTION_CTA = {
    label: "Download",
    sublabel: "Client v563",
    href: "/download"
}
